import { useState } from 'react';
import { AlertCircle, Loader2, X } from 'lucide-react';
import { useRefresh } from '@signalvote/data';
import { CAPABILITY_TIER_LABELS, CapabilityTier } from '@signalvote/shared';
import { useCampaignStore } from '@/stores';
import { useAuth } from '@/contexts/AuthContext';
import { useScoutAccess, useUpdateScoutAccess } from '@/hooks/queries';
import { failureOf } from '@/lib/data';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { viewScoutAccess, type ScoutAccessDraft } from './scoutAccessDraft';

const CONFLICT_MESSAGE = 'Someone else changed Scout access. Reloaded the latest.';

export function ScoutAccessCard() {
  const { user } = useAuth();
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);

  if (!user?.isPlatformAdmin || !selectedCampaign) return null;

  // Keyed so switching campaigns drops the previous campaign's draft.
  return <ScoutAccessEditor key={selectedCampaign.id} campaignId={selectedCampaign.id} />;
}

function ScoutAccessEditor({ campaignId }: { campaignId: string }) {
  const read = useScoutAccess(campaignId);
  const { mutateAsync: update, isPending: saving } = useUpdateScoutAccess();
  const refresh = useRefresh();
  const [draft, setDraft] = useState<ScoutAccessDraft | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (read.status !== 'ready') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Scout</CardTitle>
        </CardHeader>
        <CardContent>
          {read.status === 'failed' || read.status === 'offline' ? (
            <div role="alert" className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
              {read.status === 'offline'
                ? "You're offline, so the assistant's access settings can't load. They show when you reconnect."
                : "Couldn't load the assistant's access settings. Try again shortly."}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  const access = read.data;
  const view = viewScoutAccess(access, draft);
  const userIds = view.listed.map((m) => m.userId);
  const edit = (next: Partial<ScoutAccessDraft>) => {
    setError(null);
    setDraft({ enabled: view.enabled, userIds, ...next });
  };

  const save = async () => {
    setError(null);
    try {
      await update({ campaignId, request: view.request });
      setDraft(null);
    } catch (e) {
      if (failureOf(e).reason.kind === 'stale') {
        setDraft(null);
        setError(CONFLICT_MESSAGE);
        void refresh(useScoutAccess, campaignId);
      } else {
        setError(e instanceof Error ? e.message : "Couldn't save Scout access. Try again.");
      }
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Scout</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            Turns the campaign assistant on or off for this campaign.
          </p>
          <Switch
            checked={view.enabled}
            onCheckedChange={(enabled) => edit({ enabled })}
            disabled={saving}
          />
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">Allowlist</p>
          <p className="text-xs text-muted-foreground">Empty list = everyone with fundraising access</p>
          {view.listed.length > 0 && (
            <ul className="divide-y rounded-md border">
              {view.listed.map((m) => (
                <li key={m.userId} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{m.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{m.email}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge variant="secondary">{CAPABILITY_TIER_LABELS[m.tiers.fundraising ?? CapabilityTier.None]}</Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      aria-label={`Remove ${m.name}`}
                      onClick={() => edit({ userIds: userIds.filter((id) => id !== m.userId) })}
                      disabled={saving}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {view.addable.length > 0 && (
            <Select
              value=""
              onValueChange={(id) => edit({ userIds: [...userIds, id] })}
              disabled={saving}
            >
              <SelectTrigger>
                <SelectValue placeholder="Add a member" />
              </SelectTrigger>
              <SelectContent>
                {view.addable.map((m) => (
                  <SelectItem key={m.userId} value={m.userId}>
                    {m.name} ({m.email}), {CAPABILITY_TIER_LABELS[m.tiers.fundraising ?? CapabilityTier.None]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={save} disabled={!view.dirty || saving}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setDraft(null);
              setError(null);
            }}
            disabled={!view.dirty || saving}
          >
            Reset
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
